import { inject, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { Injectable } from '@banta/common';
import { DevToolsAction, DevToolsMenu, DevToolsMenuItem } from '@tytapp/app/dev';
import { environment } from '@tytapp/environment';
import { isClientSide, isServerSide } from '@tytapp/environment-utils';
import { LoggerService } from './logger.service';
import { Subject } from 'rxjs';
import { LocalStorageService } from './local-storage.service';
import { MatSnackBar } from '@angular/material/snack-bar';

interface Environment {
    id: string;
    label: string;
    icon: string;
}

const ENVIRONMENTS: Environment[] = [
    { id: '', label: 'Default', icon: 'code' },
    { id: 'staging', label: 'Staging', icon: 'approval' },
    { id: 'production', label: 'Production', icon: 'precision_manufacturing' },
];

const DEFAULT_PINS = ['environments'];

@Injectable()
export class DevToolsService {
    private injector = inject(Injector);
    private logger = inject(LoggerService);
    private localStorage = inject(LocalStorageService);
    private snackbar = inject(MatSnackBar);

    constructor() {
        // This avoids the circular dependency
        this.logger.registerDevTools(this);

        if (isClientSide()) {
            if (localStorage.getItem('tyt-api-switching-environments') === '1') {
                localStorage.removeItem('tyt-api-switching-environments');
                this.firstBootAfterEnvironmentChange = true;
            }

            setTimeout(() => this.loadPinnedMenus(), 500);
        }
    }

    createLocalStorageToggle(
        item: Omit<DevToolsMenu, 'items' | 'type' | 'hidden'> & {
            localStorageKey: string,
            /**
             * When true, reloads when this flag is changed. Defaults to true.
             */
            reloadWhenChanged?: boolean,
            /**
             * When true, the flag is removed from local storage when turned off,
             * instead of setting it to a definite value ('1')
             */
            clearOnDisable?: boolean,
            getDefault: () => boolean,
            onChange?: (value: boolean | undefined) => void;
        }
    ) {
        const props = { ...item };
        delete props.localStorageKey;
        delete props.reloadWhenChanged;

        function getState() {
            if (isClientSide())
                return localStorage[item.localStorageKey];
        }

        function setState(value) {
            if (value === false && item.clearOnDisable) {
                delete localStorage[item.localStorageKey];
            } else {
                localStorage[item.localStorageKey] = value ? '1' : '0';
            }
            item.onChange?.(value);
            if (item.reloadWhenChanged !== false)
                location.reload();
        }

        function resetState() {
            delete localStorage[item.localStorageKey];
            item.onChange?.(undefined);
            if (item.reloadWhenChanged !== false)
                location.reload();
        }

        if (item.clearOnDisable) {
            return this.createToggle({
                ...props,
                getState,
                setState
            });
        } else {
            return this.createToggle({
                ...props,
                getState,
                setState,
                resetState
            });
        }
    }

    createToggle(
        item: Omit<DevToolsMenu, 'items' | 'type' | 'hidden'> & {
            getState: () => boolean | string | undefined,
            getDefault: () => boolean,
            setState(value: boolean): void,
            resetState?();
        }
    ): DevToolsMenu {
        const props = { ...item };
        delete props.setState;
        delete props.getState;

        function getState() {
            let value = item.getState();
            if (value === '1')
                return true;
            if (value === '0')
                return false;
            if (value === undefined)
                return undefined;

            return Boolean(value);
        }

        let label = item.label;
        let pinnedLabel = item.pinnedLabel;

        let menu: DevToolsMenu;
        const onItem: DevToolsAction = {
            type: 'action',
            label: 'Turn On',
            icon: 'toggle_on',
            handler: (_, i) => (item.setState(true), updateMenu())
        };
        const offItem: DevToolsAction = {
            type: 'action',
            label: 'Turn Off',
            icon: 'toggle_off',
            handler: (_, i) => (item.setState(false), updateMenu())
        };
        const resetItem: DevToolsAction = {
            type: 'action',
            label: 'Reset',
            icon: 'highlight_off',
            handler: (_, i) => (item.resetState(), updateMenu())
        };

        function updateMenu() {
            onItem.hidden = getState() === true;
            offItem.hidden = getState() === false;
            resetItem.hidden = getState() === undefined;

            const effectiveState = getState() ?? item.getDefault?.() ?? false;

            menu.iconColor = effectiveState ? 'lightgreen' : 'pink';
            menu.iconOpacity = getState() === undefined ? 0.5 : 1;
            menu.icon = effectiveState ? 'toggle_on' : 'toggle_off';
        }

        menu = {
            ...props,
            type: 'menu',
            items: [
                onItem,
                offItem
            ]
        };

        if (!menu.pinnedIcon)
            menu.pinnedIcon = menu.icon;

        updateMenu();

        if (item.resetState) {
            menu.items.push(resetItem);
        }

        return menu;
    }

    pinnedMenusLoaded = false;

    /**
     * This is true when this is the first boot after changing environments. Useful for services which must perform
     * work after the app has rebooted following an environment change.
     */
    firstBootAfterEnvironmentChange = false;

    private onEnvironmentWillChange: ((id: string) => Promise<void>)[] = [];

    /**
     * Register a task which will run (and must first complete) before an environment change will succeed.
     * Useful for cases where an asynchronous task must be run before the application is reloaded after the environment
     * changes.
     * @param runTask
     */
    beforeEnvironmentChanges(runTask: (id: string) => Promise<void>) {
        this.onEnvironmentWillChange.push(runTask);
    }

    private async runPreEnvironmentChangeTasks(newEnvironmentId: string) {
        await Promise.all(this.onEnvironmentWillChange.map(x => x(newEnvironmentId)));
    }

    triggerActionById(id: string) {
        let action = this.getActionById(id);
        if (!action) {
            this.logger.error(`No dev action with ID '${id}'`);
        } else {
            this.logger.warning(`Running dev action with ID '${id}'`);
            action.handler(action, this.injector);
        }
    }

    getItemById(id: string) {
        let selectedItem: DevToolsMenuItem;
        this.visitMenu(menuItem => {
            if (menuItem.id === id) {
                selectedItem = menuItem;
                return false;
            } else {
                return true;
            }
        });

        return selectedItem;
    }

    appendInto(id: string, items: DevToolsMenuItem[]) {
        let found = !this.visitMenu((menuItem) => {
            if (menuItem.type === 'menu' && menuItem.id === id) {
                menuItem.items.push(...items);
                return false;
            } else {
                return true;
            }
        });

        if (!found && environment.showDevTools)
            throw new Error(`DevTools: No such menu item '${id}' exists!`);

        this.loadPinnedMenus();
    }

    prependInto(id: string, items: DevToolsMenuItem[]) {
        let found = !this.visitMenu((menuItem) => {
            if (menuItem.type === 'menu' && menuItem.id === id) {
                menuItem.items.unshift(...items);
                return false;
            } else {
                return true;
            }
        });

        if (!found && environment.showDevTools)
            throw new Error(`DevTools: No such menu item '${id}' exists!`);

        this.loadPinnedMenus();
    }

    insertAfter(id: string, items: DevToolsMenuItem[]) {
        let found = !this.visitMenu((menuItem, menu) => {
            if (menuItem.type === 'action' && menuItem.id === id) {
                let index = menu.items.indexOf(menuItem);
                menu.items.splice(index + 1, 0, ...items);
                return false;
            } else {
                return true;
            }
        });

        if (!found && environment.showDevTools)
            throw new Error(`DevTools: No such menu item '${id}' exists!`);

        this.loadPinnedMenus();
    }

    insertBefore(id: string, items: DevToolsMenuItem[]) {
        let found = !this.visitMenu((menuItem, menu) => {
            if (menuItem.type === 'action' && menuItem.id === id) {
                let index = menu.items.indexOf(menuItem);
                menu.items.splice(index, 0, ...items);
                return false;
            } else {
                return true;
            }
        });

        if (!found && environment.showDevTools)
            throw new Error(`DevTools: No such menu item '${id}' exists!`);

        this.loadPinnedMenus();
    }

    getActionById(id: string) {
        let selectedItem: DevToolsAction;
        this.visitMenu(menuItem => {
            if (menuItem.type === 'action' && menuItem.id === id) {
                selectedItem = menuItem;
                return false;
            } else {
                return true;
            }
        });

        return selectedItem;
    }

    getMenuById(id: string) {
        let selectedItem: DevToolsMenu;
        this.visitMenu(menuItem => {
            if (menuItem.type === 'menu' && menuItem.id === id) {
                selectedItem = menuItem;
                return false;
            } else {
                return true;
            }
        });

        return selectedItem;
    }

    visitMenu(visitor: (item: DevToolsMenuItem, parent: DevToolsMenu) => boolean, item: DevToolsMenuItem = this.rootMenu, parent: DevToolsMenu = null) {
        if (!visitor(item, parent))
            return false;

        if (item.type === 'menu') {
            for (let subItem of item.items) {
                if (!this.visitMenu(visitor, subItem, item))
                    return false;
            }
        }

        return true;
    }

    private initialized = false;

    initializeMenu() {
        this.visitMenu((item, parent) => {
            if (item.type === 'action') {
                item.init?.(item, this.injector);
            }
            return true;
        });

        this.loadPinnedMenus();
    }

    rootMenu: DevToolsMenu = {
        type: 'menu',
        icon: 'code',
        id: 'root',
        label: 'Dev Tools',
        items: [
            {
                type: 'action',
                label: 'Dev Tools',
                icon: 'close',
                handler: () => environment.showDevTools = false
            },
            {
                id: 'navigation',
                type: 'menu',
                label: 'Navigation',
                icon: 'link',
                items: [
                    {
                        type: 'action',
                        label: 'Reload',
                        icon: 'refresh',
                        handler: () => window.location.reload()
                    },
                    {
                        type: 'action',
                        label: 'Back',
                        icon: 'arrow_back',
                        handler: () => window.history.back()
                    },
                    {
                        type: 'action',
                        get label() {
                            return `Show URL`
                        },
                        icon: 'link',
                        handler: () => alert(window.location.href)
                    },
                    {
                        type: 'action',
                        get label() {
                            return `Copy URL`
                        },
                        icon: 'content_copy',
                        handler: () => navigator.clipboard.writeText(window.location.href)
                    },
                    {
                        type: 'action',
                        label: 'Open External Page',
                        icon: 'link',
                        handler: () => window.location.assign('https://google.com')
                    },
                    {
                        type: 'action',
                        label: 'Open External in New Tab',
                        icon: 'link',
                        handler: () => window.open('https://google.com', '_blank', 'noopener')
                    },
                    {
                        type: 'action',
                        label: 'Test 404-based Redirection',
                        icon: 'public',
                        handler: (item, injector) => injector.get(Router).navigateByUrl('/engn-test-redirect')
                    },
                    {
                        type: 'menu',
                        id: 'testAreas',
                        label: 'Test Areas',
                        icon: 'science',
                        items: [
                            {
                                type: 'action',
                                label: 'Comments',
                                handler: (item, injector) => injector.get(Router)
                                    .navigateByUrl('/engineering/comments')
                            },
                            {
                                type: 'action',
                                label: 'Style Guide',
                                handler: (item, injector) => injector.get(Router)
                                    .navigateByUrl('/engineering/style-guide')
                            },
                            {
                                type: 'action',
                                label: 'Panic',
                                handler: (item, injector) => injector.get(Router)
                                    .navigateByUrl('/engineering/panic')
                            },
                            {
                                type: 'action',
                                label: 'Login Status',
                                handler: (item, injector) => injector.get(Router)
                                    .navigateByUrl('/engineering/login-status')
                            },
                            {
                                type: 'action',
                                label: 'Link Testing',
                                handler: (item, injector) => injector.get(Router)
                                    .navigateByUrl('/engineering/link-testing')
                            },
                            {
                                type: 'action',
                                label: 'Link Hash Testing',
                                handler: (item, injector) => injector.get(Router)
                                    .navigateByUrl('/engineering/link-hash-testing')
                            },
                            {
                                type: 'action',
                                label: 'Notifications',
                                handler: (item, injector) => injector.get(Router)
                                    .navigateByUrl('/engineering/notifications')
                            },
                        ]
                    }
                ]
            },
            {
                type: 'menu',
                id: 'global',
                label: 'Global',
                icon: 'settings',
                items: [
                    {
                        type: 'action',
                        label: 'Show Dev Notes',
                        icon: 'bug_report',
                        get hidden() {
                            return environment.showDevNotes;
                        },
                        handler: (injector, item) => {
                            environment.showDevNotes = true;
                        }
                    },
                    {
                        type: 'action',
                        label: 'Hide Dev Notes',
                        icon: 'bug_report',
                        get hidden() {
                            return !environment.showDevNotes;
                        },
                        handler: (injector, item) => {
                            environment.showDevNotes = false;
                        }
                    },
                    {
                        type: 'menu',
                        id: 'environments',
                        label: 'Environments',
                        icon: 'burst_mode',
                        items: ENVIRONMENTS.map(env => ({
                            type: 'action',
                            id: `env:${env.id}`,
                            icon: env.icon,
                            label: env.label,
                            handler: async () => {
                                if (isServerSide())
                                    return;

                                // Update the current API override. We also update environment.apiOverride immediately--
                                // this is mostly to simplify user switching logic in UserService.

                                localStorage.setItem('tyt-api-override', env.id);
                                localStorage.setItem('tyt-api-switching-environments', '1');
                                environment.apiOverride = env.id;

                                // Notify any services that need to update state or caches and wait for their tasks
                                // to complete.

                                await this.runPreEnvironmentChangeTasks(env.id);
                                window.location.reload();
                            }
                        }))
                    },
                    {
                        type: 'menu',
                        id: 'native-emulation',
                        label: 'Emulate Native',
                        icon: 'smartphone',
                        items: [
                            {
                                type: 'action',
                                label: 'No Emulation',
                                handler(action, injector) {
                                    delete localStorage['tyt-native-build-override'];
                                    delete localStorage['tyt-product-name-override'];
                                    location.reload();
                                }
                            },
                            {
                                type: 'action',
                                label: 'TYT for Android',
                                handler(action, injector) {
                                    localStorage['tyt-native-build-override'] = '1';
                                    localStorage['tyt-product-name-override'] = 'TYT for Android';
                                    location.reload();
                                }
                            },
                            {
                                type: 'action',
                                label: 'TYT for iOS',
                                handler(action, injector) {
                                    localStorage['tyt-native-build-override'] = '1';
                                    localStorage['tyt-product-name-override'] = 'TYT for iOS';
                                    location.reload();
                                }
                            }
                        ]
                    },
                    {
                        type: 'menu',
                        id: 'connectivity',
                        label: 'Connectivity',
                        icon: 'network_check',
                        items: [
                            {
                                type: 'action',
                                label: 'Auto (No emulation)',
                                icon: 'radar',
                                handler: (injector, item) => {
                                    delete localStorage['tyt:online'];
                                    location.reload();
                                }
                            },
                            {
                                type: 'action',
                                label: 'Emulate Offline',
                                icon: 'cloud_off',
                                handler: (injector, item) => {
                                    localStorage['tyt:online'] = '0';
                                    location.reload();
                                }
                            },
                            {
                                type: 'action',
                                label: 'Emulate Online',
                                icon: 'cloud',
                                handler: (injector, item) => {
                                    localStorage['tyt:online'] = '1';
                                    location.reload();
                                }
                            },
                        ]
                    },
                    {
                        type: 'action',
                        label: 'Force panic',
                        icon: 'priority_high',
                        handler() {
                            throw new Error("This is a forced panic for development purposes.")
                        }
                    }
                ]
            }
        ]
    };

    pinnedMenus: DevToolsMenu[] = [];

    isMenuPinned(menu: DevToolsMenu) {
        return this.pinnedMenus.some(x => x.id === menu.id);
    }

    pinMenu(menu: DevToolsMenu) {
        if (!menu.id) {
            this.snackbar.open(`Cannot pin menu: No ID assigned!`, undefined, { duration: 3000, panelClass: 'error' });
            return;
        }

        if (this.pinnedMenus.some(x => x.id === menu.id))
            return;

        this.pinnedMenus.push(menu);
        this.savePinnedMenus();
    }

    unpinMenu(menu: DevToolsMenu) {
        let index = this.pinnedMenus.findIndex(x => x.id === menu.id);
        this.pinnedMenus.splice(index, 1);
        this.savePinnedMenus();
    }

    resetPinnedMenus() {
        if (isClientSide()) {
            this.localStorage.remove(`devtools:pinned-menus`);
        }
        this.loadPinnedMenus();
    }

    private savePinnedMenus() {
        if (isClientSide()) {
            this.localStorage.set(`devtools:pinned-menus`, this.pinnedMenus.map(x => x.id));
        }
    }

    private loadPinnedMenusDeferred;
    private loadPinnedMenus() {
        if (!isClientSide())
            return;

        clearTimeout(this.loadPinnedMenusDeferred);
        this.loadPinnedMenusDeferred = setTimeout(() => {
            let pinnedIds: string[] = DEFAULT_PINS;
            let foundMenus = {} as Record<string,DevToolsMenu>;

            if (isClientSide()) {
                let persistedIds = this.localStorage.get(`devtools:pinned-menus`) as string[];
                if (persistedIds)
                    pinnedIds = persistedIds;
            }

            this.visitMenu((item, parent) => {
                if (item.type !== 'menu')
                    return true;

                if (pinnedIds.includes(item.id))
                    foundMenus[item.id] = item;
                return true;
            });

            this.pinnedMenus = pinnedIds.map(x => foundMenus[x]).filter(x => x);
            this.pinnedMenusLoaded = true;
        }, 400);
    }
}
